import type { SearchInfoData } from '@/app/search/types.ts';
import type { InitialParams } from '@/app/components/hotel-detail/InitialParamsProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { getTransportCompanyAndRouteParams } from '@/app/components/hotel-detail/common/tracking.ts';
import { getTrackingVcSearchId } from '@/app/common/tracking/trackingUtils.ts';
import type { OffersData } from '@/app/components/hotel-detail/OffersProvider.tsx';
import type { CartCreationResponse } from '@/app/api/hotel-detail/cart-creation/route.ts';
import type { BookRoomProps } from '@/app/components/hotel-detail/hooks/useBookRoom.ts';

export const CLICK2CHECKOUT = 'CLICK2CHECKOUT';
export const CLICK2CHECKOUT_VERSION = '1.5';

export type ClickToCheckoutBasePayload = {
  actionId: string;
  desiredProfitPricingId: string;
  inboundAirlineId: string;
  inboundRouteId: string;
  internalHotelId: number;
  mealPlanClass: string;
  optionIndex: number;
  outboundAirlineId: string;
  outboundRouteId: string;
  productDetailKey: string;
  providerId: string;
  roomId: string;
  searchId: string;
  vcSearchId: string;
  flightClickId: string;
  clickId: string;
};

export type ClickToCheckoutPayload = {
  cartId: string;
  tripId: string;
  freeCancellation: boolean;
  rateId: string;
} & ClickToCheckoutBasePayload;

export type ClickToCheckoutParams = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  initialParams: InitialParams;
  cartCreationResponse: CartCreationResponse | null | undefined;
  bookRoomProps: Pick<BookRoomProps, 'mealPlan' | 'roomId'>;
  offersData: OffersData | undefined;
};

export const createClickToCheckoutPayload = ({
  searchParams,
  searchInfoData,
  initialParams,
  cartCreationResponse,
  bookRoomProps,
  offersData,
}: ClickToCheckoutParams): ClickToCheckoutPayload => {
  const { mealPlan: selectedMealPlan, roomId } = bookRoomProps;
  const { searchId } = searchInfoData;
  const vcSearchId = searchParams[URLParameters.VC_SEARCH_ID];

  return {
    searchId: searchId || '',
    productDetailKey: initialParams.productDetailKey,
    actionId: cartCreationResponse?.actionId || '',
    internalHotelId: Number(initialParams.internalHotelId),
    providerId: selectedMealPlan.supplier?.id?.toString(),
    desiredProfitPricingId: selectedMealPlan.dynamicPricingId,
    mealPlanClass: selectedMealPlan.boardClass,
    optionIndex: Number(selectedMealPlan.index || 0),
    roomId,
    vcSearchId: getTrackingVcSearchId(offersData, vcSearchId),
    ...getTransportCompanyAndRouteParams(offersData?.transport?.transports?.[0]),
    cartId: cartCreationResponse?.cartId || '',
    flightClickId: initialParams.flightClickId || '',
    clickId: searchParams[URLParameters.CLICK_ID] || '',
    tripId: offersData?.transport?.transports?.[0]?.id || '',
    freeCancellation: selectedMealPlan.cancellationPolicy.type === 'REFUNDABLE',
    rateId: selectedMealPlan.id,
  };
};
